import { useEffect, useRef } from 'react';

import { usePlayer } from '../../stores/player';
import { useRoomTuning } from '../../stores/room';
import { correction, positionNow } from './sync';

/** How often drift is measured. Four times a second is well below audible. */
const TICK_MS = 250;

/**
 * Keeps the one `<audio>` element in step with a room (§6.8).
 *
 * ## Why this lives beside the element rather than in the room page
 *
 * The correction has to touch `currentTime` and `playbackRate`, and the element
 * is owned by `PlayerHost` and never unmounts. Doing it from the room page
 * would mean the audio drifted free the moment somebody navigated away while
 * still listening - which is exactly the case §9.1 exists to protect.
 *
 * ## What it does, four times a second
 *
 * Works out where the room *should* be from the last known position and the
 * clock offset, compares it to where the audio actually is, and applies the
 * smallest correction that closes the gap:
 *
 * - under 120ms: nothing, and the rate goes back to 1
 * - under 1.5s: bend the playback rate by up to 4%, which nobody can hear
 * - beyond that: seek, because rate alone would take a minute to catch up
 *
 * The middle case is the whole point. Two browsers decoding the same stream are
 * never sample-accurate, and correcting every small difference with a seek is
 * audible as a stutter - far worse than being 200ms behind.
 */
export function useRoomSync(audioRef: React.RefObject<HTMLAudioElement | null>): void {
  const tuned = useRoomTuning((state) => state.tuned);
  const rateRef = useRef(1);

  useEffect(() => {
    const audio = audioRef.current;

    if (!audio || tuned === null) {
      /*
       * Untuned playback runs at normal speed.
       *
       * Without this, leaving a room mid-correction leaves the element at
       * 1.04x for ever, and every track afterwards is very slightly sharp -
       * the sort of bug somebody reports as "the site sounds wrong".
       */
      if (audio && rateRef.current !== 1) {
        audio.playbackRate = 1;
        rateRef.current = 1;
      }

      return;
    }

    const tick = () => {
      const { playhead } = useRoomTuning.getState();
      const player = usePlayer.getState();

      if (!playhead || playhead.position_ms === null) return;

      /*
       * A track change is not a drift.
       *
       * The room has moved on and this element is still on the old track;
       * seeking would put the wrong audio at the right timestamp. The component
       * watching the room loads the new one - all this can do is not make it
       * worse.
       */
      if (playhead.track_id !== null && player.track?.id !== playhead.track_id) return;

      // A paused room stays where it is, and so does the listener.
      if (playhead.paused) {
        if (!audio.paused) audio.pause();

        return;
      }

      if (audio.paused && player.playing) {
        void audio.play().catch(() => {});
      }

      const target = positionNow(playhead);

      if (target === null) return;

      // Nothing to correct against until the element has decoded something.
      if (audio.readyState < 1) return;

      const next = correction(audio.currentTime * 1000, target);

      if (next.kind === 'seek') {
        audio.currentTime = next.to / 1000;
        audio.playbackRate = 1;
        rateRef.current = 1;

        return;
      }

      if (next.kind === 'rate' && Math.abs(next.rate - rateRef.current) > 0.001) {
        audio.playbackRate = next.rate;
        rateRef.current = next.rate;
      }
    };

    tick();
    const timer = setInterval(tick, TICK_MS);

    return () => {
      clearInterval(timer);

      if (audioRef.current) {
        audioRef.current.playbackRate = 1;
        rateRef.current = 1;
      }
    };
  }, [tuned, audioRef]);
}
